import SparkMD5 from 'spark-md5';
import { RECORD_DATA_TYPE, RECORD_DATA_TYPE_LABEL } from '@demandproai/shared';
import {
  EASY_MODE_ACCEPTED_EXTENSIONS,
  EASY_MODE_MAX_FILE_COUNT,
  EASY_MODE_MAX_FILE_SIZE_BYTES,
  EASY_MODE_REMOVABLE_STATUSES,
  EASY_MODE_UPLOAD_STATUS
} from '../constants/easyModeDocuments';

const readEntryAsFile = (entry) =>
  new Promise((resolve) => entry.file(resolve, () => resolve(null)));

const readDirectoryBatch = (reader) =>
  new Promise((resolve) => reader.readEntries(resolve, () => resolve([])));

const joinFolderPath = ({ folderPath, name }) => (folderPath ? `${folderPath}/${name}` : name);

// readEntries yields at most 100 entries per call, so it has to be drained in a loop
const walkEntry = async ({ entry, folderPath = '' }) => {
  if (!entry) return [];

  if (entry.isFile) {
    const file = await readEntryAsFile(entry);

    return file ? [{ file, folderPath }] : [];
  }

  const childFolderPath = joinFolderPath({ folderPath, name: entry.name });
  const reader = entry.createReader();
  const collected = [];
  let batch = await readDirectoryBatch(reader);

  while (batch.length > 0) {
    for (const child of batch) {
      collected.push(...(await walkEntry({ entry: child, folderPath: childFolderPath })));
    }

    batch = await readDirectoryBatch(reader);
  }

  return collected;
};

// A picked folder puts the relative path on the File itself; a dropped folder only exposes it on the
// directory entry, so both paths are normalised to the same { file, folderPath } candidate.
const getFolderPathFromFile = (file) => {
  const segments = (file?.webkitRelativePath ?? '').split('/').filter(Boolean);

  return segments.slice(0, -1).join('/');
};

export const toUploadCandidate = (item) =>
  item?.file ? item : { file: item, folderPath: getFolderPathFromFile(item) };

export const toUploadCandidates = (items = []) => Array.from(items).map(toUploadCandidate);

// the deepest folder is the one that says something: "Case/Medical/Harkins" is filed under Harkins
export const getFolderName = ({ folderPath }) => folderPath?.split('/').filter(Boolean).at(-1) ?? '';

// Everything the row needs to render, derived here so the row component stays dumb
export const getUploadRowView = ({ row }) => ({
  // UNCLASSIFIED has no label on purpose - a tag can only exist once the classifier has resolved one
  tagLabel: RECORD_DATA_TYPE_LABEL[row.recordType],
  isUnrecognized: row.recordType === RECORD_DATA_TYPE.OTHER,
  hasFailed: row.status === EASY_MODE_UPLOAD_STATUS.FAILED,
  // the bytes are already in S3, so re-uploading them would only produce the same unreadable record
  isUnreadable: row.status === EASY_MODE_UPLOAD_STATUS.BAD_RECORD,
  // the deepest folder only, so a nested path does not crowd out the filename
  folderName: getFolderName({ folderPath: row.folderPath }),
  isRemovable: EASY_MODE_REMOVABLE_STATUSES.includes(row.status)
});

export const getCandidateIdentity = ({ file, folderPath }) =>
  joinFolderPath({ folderPath, name: file.name });

// md5 of the file's bytes, read in chunks so a large document never sits in memory whole. This is
// content identity, not name: two files with the same bytes collide here even under different names.
export const computeFileMd5 = (file) =>
  new Promise((resolve, reject) => {
    const chunkSize = 2 * 1024 * 1024;
    const spark = new SparkMD5.ArrayBuffer();
    const reader = new FileReader();
    let cursor = 0;

    const readNextChunk = () => reader.readAsArrayBuffer(file.slice(cursor, cursor + chunkSize));

    reader.onload = ({ target }) => {
      spark.append(target.result);
      cursor += chunkSize;

      if (cursor < file.size) readNextChunk();
      else resolve(spark.end());
    };
    reader.onerror = () => reject(reader.error);

    readNextChunk();
  });

// S3 user metadata rides in a header and has to stay printable ASCII, so an accented folder name
// travels stripped rather than failing the whole upload. The row keeps the real name for display.
export const toMetaDataSafeFolderPath = ({ folderPath = '' }) =>
  folderPath.replace(/[^\x20-\x7E]/g, '').trim();

// Entries must be taken off the DataTransfer synchronously, before the first await, or the browser
// has already recycled the item list.
export const collectFilesFromDataTransfer = async ({ dataTransfer }) => {
  const items = Array.from(dataTransfer?.items ?? []);
  const entries = items
    .filter((item) => item.kind === 'file')
    .map((item) => (item.webkitGetAsEntry ? item.webkitGetAsEntry() : null));

  if (entries.every((entry) => entry === null)) {
    return toUploadCandidates(dataTransfer?.files ?? []);
  }

  const fileGroups = await Promise.all(entries.map((entry) => walkEntry({ entry })));

  return fileGroups.flat();
};

export const getFileExtension = ({ fileName }) => {
  const dotIndex = fileName.lastIndexOf('.');

  return dotIndex === -1 ? '' : fileName.slice(dotIndex).toLowerCase();
};

const toMegabytes = (bytes) => Math.round(bytes / (1024 * 1024));

// name duplication is not validated here: a repeated name is a legitimate document, and every
// upload gets its own UUID S3 key regardless, so the only guards left are type, size and the count cap
export const filterAcceptedFiles = ({
  files,
  existingFileKeys = [],
  maxFileCount = EASY_MODE_MAX_FILE_COUNT,
  maxFileSizeBytes = EASY_MODE_MAX_FILE_SIZE_BYTES
}) => {
  const existingCount = existingFileKeys.length;
  const acceptedFiles = [];
  const rejections = [];

  for (const candidate of toUploadCandidates(files)) {
    const { file } = candidate;

    if (existingCount + acceptedFiles.length >= maxFileCount) {
      rejections.push({
        fileName: file.name,
        reason: `Over the ${maxFileCount} document limit for one case`
      });
      continue;
    }

    if (!EASY_MODE_ACCEPTED_EXTENSIONS.includes(getFileExtension({ fileName: file.name }))) {
      rejections.push({ fileName: file.name, reason: 'Unsupported file type' });
      continue;
    }

    if (file.size === 0) {
      rejections.push({ fileName: file.name, reason: 'File is empty' });
      continue;
    }

    if (file.size > maxFileSizeBytes) {
      rejections.push({
        fileName: file.name,
        reason: `Over the ${toMegabytes(maxFileSizeBytes)} MB file size limit`
      });
      continue;
    }

    acceptedFiles.push(candidate);
  }

  return { acceptedFiles, rejections };
};
